import { Box, Grid, IconButton } from "@mui/material";
import { ReactComponent as SharelinkDesktop } from "assets/SharelinkDesktop.svg";
import { ReactComponent as SharelinkMobile } from "assets/SharelinkMobile.svg";

import { searchSelectedTab, setInnCode, setLibraryRecentSearch } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import InncodeDropDown from "components/common/dropdowns/InncodeDropDown";
import { resetAllModifiedFilters } from "components/common/Filters/GlobalFilters.slice";
import SearchAppBar from "components/common/searchBar";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Tabs from "components/common/Tabs";
import Typography from "components/common/Typography";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { minSearchStrLength } from "utils/CommonConstants";
import { planTypes, responseTypes } from "utils/Types";
import { tabDetails } from "../functionalArea/TabsConfig";
import { getSharedLink, postSharedLink } from "../Library.slice";
import "./LibrarySearch.scss";

function LibraryHallSearch() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { isMobileScreen } = useContext(ThemeContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isParamsSet, setIsParamsSet] = useState(false);

  const selectedTab = useAppSelector((state) => state.app.searchSelectedTab);
  const innCode = useAppSelector((state: any) => state.app.innCode);
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const plansTotalRecords = useAppSelector((state) => state.plans.totalRecords);
  const actionsTotalRecords = useAppSelector((state: any) => state.individualAction.totalRecords);
  const libraryRecentSearch = useAppSelector((state) => state.app.libraryRecentSearch);

  const backURL = "/library";
  const searchText = searchParams.get("q") as string;
  const uuid = searchParams.get("uuid") as string;
  const cardId = searchParams.get("cardid") as string;

  useEffect(() => {
    setIsParamsSet(false);

    const getSearchDetails = async () => {
      const response: any = await dispatch(
        getSharedLink({
          id: uuid
        })
      );

      if (response.payload.searchText) {
        const params = new URLSearchParams(searchParams);
        params.set("q", response.payload.searchText);
        setSearchParams(params);
      }
      setIsParamsSet(true);
    };

    if (uuid) getSearchDetails();
    else setIsParamsSet(true);
  }, []);

  useEffect(() => {
    dispatch(searchSelectedTab(cardId || location.state?.cardId ? 1 : 0));
  }, [cardId]);

  const handleOnChange = (value: string) => {
    dispatch(setInnCode(value));
    localStorage.setItem("innCode", value);
    dispatch(resetAllModifiedFilters());
  };

  const getInputDetails = (inputText: string) => {
    if (inputText.length > minSearchStrLength || !inputText)
      navigate(`/library/search?q=${encodeURIComponent(inputText)}&tab=${searchParams.get("tab")}`, {
        state: { backURL: backURL },
        replace: true
      });
  };

  const handleTabSelection = (x: number) => {
    dispatch(searchSelectedTab(x));
  };

  const generateURL = async () => {
    const isSearchInvalid =
      !searchText || (selectedTab === 0 && !plansTotalRecords) || (selectedTab === 1 && !actionsTotalRecords);

    if (isSearchInvalid) {
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          variant: "error",
          setState: setFeedbackDetails({ shouldShow: false }),
          message: searchText
            ? "There are no search results to be copied."
            : "Please enter search text to create a search link. "
        })
      );
      return;
    }

    const response: any = await dispatch(
      postSharedLink({
        type: "library_search",
        searchText,
        innCode
      })
    );

    if (response.payload.status < responseTypes.success_threshold) {
      const newUrl = `${window.location.origin}${window.location.pathname}?tab=Action+Plans&uuid=${response?.payload?.id}`;

      await navigator.clipboard.writeText(newUrl).then(() => {
        dispatch(
          setFeedbackDetails({
            shouldShow: true,
            variant: "success",
            setState: setFeedbackDetails({ shouldShow: false }),
            message: "Link Copied"
          })
        );
      });
    }
  };

  return (
    <Grid sx={{ backgroundColor: tokens.Background.General.Secondary }} className="library-search">
      <div className="library-search_titlebar">
        <Typography color={tokens.Text.General.PrimaryHeadline} showBold variant={"h3"}>
          Search in Library
        </Typography>
        <Box display={"flex"} flexDirection={"row"} alignItems={"center"} gap={"8px"}>
          <Typography variant="body2" color={tokens.Text.General.Secondary}>
            Exploring plans for
          </Typography>
          <InncodeDropDown
            parentBoxWidth="100px"
            backgroundColor={tokens.Background.General.Color}
            width={isMobileScreen ? "170px" : "284px"}
            height="24px"
            color={tokens.Text.General.Color}
            isCorp={isCorp}
            value={innCode}
            onChange={handleOnChange}
          />
        </Box>
      </div>
      <div className="library-search_actionsbar">
        <div className="library-search_searchbar">
          <SearchAppBar
            isPreDefinedNav
            getInputDetails={getInputDetails}
            value={searchText}
            planType={planTypes.AllPlans}
            divider
            libraryRecentSearch={libraryRecentSearch}
            setRecentSearch={setLibraryRecentSearch}
          />
          <div className="library-search_searchText">
            <Typography variant={"label"}>Showing search results</Typography>
            {searchText && (
              <>
                <Typography variant={"label"}>for</Typography>
                <Typography variant={"label"} showBold>
                  "{searchText}"
                </Typography>
              </>
            )}
          </div>
        </div>
        <Button
          className="library-search_desktopButton"
          startIcon={<SharelinkDesktop />}
          variant="secondary"
          label={"Copy this Search"}
          onClick={generateURL}
        />
        <IconButton className="library-search_mobileButton" onClick={generateURL}>
          <SharelinkMobile />
        </IconButton>
      </div>

      <Grid marginTop={2} item md={12}>
        {isParamsSet && (
          <Tabs
            shouldAppendTabParam
            alignItems="flex-start"
            tabDetails={tabDetails}
            defaultTab={selectedTab || 0}
            selectedTab={handleTabSelection}
            retainFilters
          />
        )}
      </Grid>
    </Grid>
  );
}

export default LibraryHallSearch;
